/**
 * The API's dates are calendar days as YYYY-MM-DD, with no time or zone: the
 * day Recharge charges, the day a pause ends, the day a card expires. They
 * are read and written as local days, never through `new Date('YYYY-MM-DD')`,
 * which is UTC midnight and a day early anywhere west of Greenwich.
 */

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/

export function parseDay(iso: string): Date {
  const match = ISO_DAY.exec(iso)
  if (!match) throw new Error(`Not a YYYY-MM-DD day: ${iso}`)
  const [, year, month, day] = match
  return new Date(Number(year), Number(month) - 1, Number(day))
}

export function toIsoDay(date: Date): string {
  const pad = (part: number) => String(part).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** "Wednesday, October 8", with the year added once it is not this one. */
export function formatDay(iso: string, today = new Date()): string {
  const date = parseDay(iso)
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    ...(date.getFullYear() !== today.getFullYear() && { year: 'numeric' }),
  }).format(date)
}

/** "12/2027", as a card prints its expiry. */
export function formatMonthYear(iso: string): string {
  const date = parseDay(iso)
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`
}

export function tomorrow(today = new Date()): string {
  const date = new Date(today)
  date.setDate(date.getDate() + 1)
  return toIsoDay(date)
}

/**
 * The same day of the month, `months` later, or the last day of that month
 * when it has no such day — so a month after January 31st is February 28th,
 * not March 3rd.
 */
export function addMonths(iso: string, months: number): string {
  const date = parseDay(iso)
  const day = date.getDate()
  date.setDate(1)
  date.setMonth(date.getMonth() + months)
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  date.setDate(Math.min(day, lastDay))
  return toIsoDay(date)
}

/** Whether a day the form offers is one Recharge can still charge on; false for anything malformed. */
export function isAfterToday(iso: string, today = new Date()): boolean {
  return ISO_DAY.test(iso) && daysUntil(iso, today) > 0
}

/** Whole days from today to the day, negative once it has passed. */
export function daysUntil(iso: string, today = new Date()): number {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round((parseDay(iso).getTime() - start.getTime()) / 86_400_000)
}
